import { DEFAULT_COUNTRY_CODE } from '@/config';
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import { convertArabicToEnglishNumbers } from './numberHelpers';

export const parsePhoneNumber = (phone?: string) => {
  const parsedPhone = parsePhoneNumberFromString('+' + cleanPhoneNumber(phone));

  return {
    phone: parsedPhone?.nationalNumber,
    countryCode: `${parsedPhone?.countryCallingCode ?? DEFAULT_COUNTRY_CODE}`,
  };
};

/**
 * Attaches a country code to a phone number after cleaning it.
 * Returns undefined for invalid inputs.
 *
 * @param {string | undefined} phoneNumber - The phone number to process
 * @param {string | undefined} countryCode - The country code to attach
 * @returns {string | undefined} The phone number with country code, or undefined if invalid
 */
export const attachCountryCodeToPhone = (phoneNumber?: string, countryCode?: string) => {
  if (!phoneNumber || !countryCode || phoneNumber == '') return undefined;
  const cleanedPhoneNumber = cleanPhoneNumber(phoneNumber);

  return cleanedPhoneNumber ? `${countryCode}${cleanedPhoneNumber}` : undefined;
};

/**
 * Cleans a phone number by removing all non-numeric characters and converting Arabic numerals.
 * Returns undefined for invalid inputs.
 *
 * @param {string | undefined} phoneNumber - The phone number to clean
 * @returns {string | undefined} The cleaned phone number, or undefined if invalid
 */
export const cleanPhoneNumber = (phoneNumber?: string): string | undefined => {
  if (!phoneNumber) return undefined;
  const cleanedValue = convertArabicToEnglishNumbers(phoneNumber).replace(/[^0-9]/g, '');
  return cleanedValue.startsWith('0') ? cleanedValue.slice(1) : cleanedValue;
};

/**
 * Formats a phone number for display (read-only surfaces only, never inputs).
 * Uses libphonenumber-js international grouping, e.g. '9647701234567' -> '+964 770 123 4567'.
 * Falls back to the default country code for bare national numbers, then to raw digits.
 *
 * @param {string | number | undefined | null} phone - The phone number to display
 * @returns {string} The formatted phone number, or '' when there is nothing to show
 */
export const formatPhoneDisplay = (phone?: string | number | null): string => {
  const digits = convertArabicToEnglishNumbers(String(phone ?? '')).replace(/[^0-9]/g, '');
  if (!digits) return '';

  const parsed = parsePhoneNumberFromString('+' + digits);
  if (parsed?.isValid()) return parsed.formatInternational();

  // Bare national number (e.g. '07701234567') — try the default country code.
  const national = digits.startsWith('0') ? digits.slice(1) : digits;
  const withDefault = parsePhoneNumberFromString(`+${DEFAULT_COUNTRY_CODE}${national}`);
  if (withDefault?.isValid()) return withDefault.formatInternational();

  return `+${digits}`;
};

/**
 * Validates a phone number with the given country code using libphonenumber-js.
 *
 * @param {string | undefined} phoneNumber - The phone number to validate
 * @param {string | undefined} countryCode - The country code (e.g., '1' for US, '98' for Iran)
 * @returns {boolean} true if the phone number is valid, false otherwise
 */
export const checkPhoneNumberValidation = (phoneNumber?: string, countryCode?: string) => {
  const fullPhoneNumber = attachCountryCodeToPhone(phoneNumber, countryCode);

  const isPhoneValid = fullPhoneNumber ? isValidPhoneNumber('+' + fullPhoneNumber) : false;
  return isPhoneValid;
};
